import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatApiError, resolveImage } from '@/lib/api';
import { Field, Area, SelectField, Card } from '@/admin/fields';

const labelCls = 'block text-xs font-mono uppercase tracking-wider text-yunti-ink/70 mb-1';
const inputCls =
  'w-full px-3 py-2.5 bg-white border border-yunti-ink/20 text-sm focus:border-yunti-purple focus:outline-none rounded-none';

const STATUS_OPTIONS = [
  { value: 'preview', label: 'Preview (shown as coming soon)' },
  { value: 'live', label: 'Live (shown with details)' },
  { value: 'unpublished', label: 'Unpublished (hidden from site)' },
];

function slugify(name) {
  return (name || 'product')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'product';
}

function formatPrice(value, currency) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(Number(value));
  } catch {
    return String(value);
  }
}

function ProductItem({ product, index, collections, onChange, onRemove }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin/upload', fd);
      onChange('image', data.path);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Card title={product.name || 'New product'}>
      <div className="flex gap-4 items-start">
        <div className="w-24 shrink-0">
          <div className="aspect-[3/4] bg-yunti-sand border border-yunti-ink/10 overflow-hidden flex items-center justify-center">
            {product.image ? (
              <img src={resolveImage(product.image)} alt={product.alt || product.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[9px] font-mono text-yunti-ink/40 text-center px-1">NO IMAGE</span>
            )}
          </div>
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2 bg-yunti-ink text-white text-[11px] font-mono uppercase tracking-widest hover:bg-yunti-purple transition-colors disabled:opacity-60"
              data-testid={`upload-product-image-${index}`}
            >
              {uploading ? 'Uploading...' : 'Upload photo'}
            </button>
            {product.image && (
              <button
                type="button"
                onClick={() => onChange('image', '')}
                className="px-3 py-2 border border-yunti-ink/30 text-[11px] font-mono uppercase tracking-widest hover:border-red-600 hover:text-red-700 transition-colors"
                data-testid={`remove-product-image-${index}`}
              >
                Remove photo
              </button>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])}
            data-testid={`file-input-${index}`}
          />
          <Field label="Or paste image URL" value={product.image} onChange={(v) => onChange('image', v)} testId={`edit-product-image-${index}`} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Product name" value={product.name} onChange={(v) => onChange('name', v)} testId={`edit-product-name-${index}`} />
        <Field label="Badge" value={product.badge} onChange={(v) => onChange('badge', v)} testId={`edit-product-badge-${index}`} />
      </div>
      <Area label="Description" rows={3} value={product.description} onChange={(v) => onChange('description', v)} testId={`edit-product-description-${index}`} />
      <Field label="Image alt text" value={product.alt} onChange={(v) => onChange('alt', v)} testId={`edit-product-alt-${index}`} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Field label="Price" value={product.price ?? ''} onChange={(v) => onChange('price', v === '' ? null : Number(v))} placeholder="45.00" testId={`edit-product-price-${index}`} />
        <Field label="Compare-at price (optional)" value={product.compareAtPrice ?? ''} onChange={(v) => onChange('compareAtPrice', v === '' ? null : Number(v))} placeholder="60.00" testId={`edit-product-compare-${index}`} />
        <Field label="Inventory (leave empty for untracked)" value={product.inventory ?? ''} onChange={(v) => onChange('inventory', v === '' ? null : Number(v))} placeholder="25" testId={`edit-product-inventory-${index}`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Sizes (comma separated)" value={(product.sizes || []).join(', ')} onChange={(v) => onChange('sizes', v.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="S, M, L, XL" testId={`edit-product-sizes-${index}`} />
        <Field label="Colors (comma separated)" value={(product.colors || []).join(', ')} onChange={(v) => onChange('colors', v.split(',').map((s) => s.trim()).filter(Boolean))} placeholder="Purple, Forest Green" testId={`edit-product-colors-${index}`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField
          label="Collection"
          value={product.collection || (collections[0]?.id ?? '')}
          onChange={(v) => onChange('collection', v)}
          options={collections.map((c) => ({ value: c.id, label: c.name }))}
          testId={`edit-product-collection-${index}`}
        />
        <SelectField label="Status" value={product.status || 'preview'} onChange={(v) => onChange('status', v)} options={STATUS_OPTIONS} testId={`edit-product-status-${index}`} />
      </div>

      <button
        type="button"
        onClick={onRemove}
        className="text-xs font-mono uppercase text-red-700 hover:underline"
        data-testid={`remove-product-${index}`}
      >
        Remove this product
      </button>
    </Card>
  );
}

export function ProductsTab({ content, update }) {
  const items = content.products.items;
  const collections = content.products.collections || [];
  const setItem = (i, key, v) => update('products.items', items.map((p, idx) => (idx === i ? { ...p, [key]: v } : p)));

  const addItem = () =>
    update('products.items', [
      ...items,
      {
        id: `${slugify('new-product')}-${Date.now()}`,
        name: 'New Product',
        badge: '',
        image: '',
        alt: '',
        collection: collections[0]?.id || 'perspective',
        description: '',
        price: null,
        compareAtPrice: null,
        sizes: [],
        colors: [],
        inventory: null,
        status: 'preview',
      },
    ]);

  const addCollection = () =>
    update('products.collections', [
      ...collections,
      { id: `${slugify('collection')}-${Date.now()}`, name: 'New Collection', note: '' },
    ]);

  const setCollection = (i, key, v) =>
    update('products.collections', collections.map((c, idx) => (idx === i ? { ...c, [key]: v } : c)));

  return (
    <div className="space-y-6">
      <Card title="Section header & storefront notice">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={content.products.eyebrow} onChange={(v) => update('products.eyebrow', v)} testId="edit-products-eyebrow" />
          <Field label="Title" value={content.products.title} onChange={(v) => update('products.title', v)} testId="edit-products-title" />
        </div>
        <Field label="Side note" value={content.products.note} onChange={(v) => update('products.note', v)} testId="edit-products-note" />
        <Field label="Notice" value={content.products.notice} onChange={(v) => update('products.notice', v)} testId="edit-products-notice" />
        <Field label="Notice accent" value={content.products.noticeAccent} onChange={(v) => update('products.noticeAccent', v)} testId="edit-products-notice-accent" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Preview label" value={content.products.previewLabel} onChange={(v) => update('products.previewLabel', v)} testId="edit-products-preview-label" />
          <Field label="Preview sub-line" value={content.products.previewSub} onChange={(v) => update('products.previewSub', v)} testId="edit-products-preview-sub" />
          <Field label="Live label" value={content.products.liveLabel} onChange={(v) => update('products.liveLabel', v)} testId="edit-products-live-label" />
          <Field label="Live sub-line" value={content.products.liveSub} onChange={(v) => update('products.liveSub', v)} testId="edit-products-live-sub" />
        </div>
      </Card>

      <Card title="Store settings" hint="Checkout stays off until you decide to activate it. Turning on prices only displays them.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <SelectField
            label="Currency"
            value={content.products.currency || 'USD'}
            onChange={(v) => update('products.currency', v)}
            options={['USD', 'EUR', 'GBP', 'CAD', 'AUD'].map((c) => ({ value: c, label: c }))}
            testId="edit-products-currency"
          />
          <label className="flex items-center gap-3 pb-2">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={!!content.products.showPrices}
              onChange={(e) => update('products.showPrices', e.target.checked)}
              data-testid="edit-products-show-prices"
            />
            <span className="text-xs font-mono uppercase tracking-wider text-yunti-ink/70">Show prices on the site</span>
          </label>
        </div>
      </Card>

      <Card title="Collections" hint="Products are grouped by collection on your store's backend.">
        {collections.map((c, i) => (
          <div key={c.id} className="flex flex-col sm:flex-row gap-3 items-start border-b border-yunti-ink/10 pb-3">
            <div className="flex-1 w-full">
              <Field label={`Collection ${i + 1} name`} value={c.name} onChange={(v) => setCollection(i, 'name', v)} testId={`edit-collection-name-${i}`} />
            </div>
            <div className="flex-1 w-full">
              <Field label="Note" value={c.note} onChange={(v) => setCollection(i, 'note', v)} testId={`edit-collection-note-${i}`} />
            </div>
            <button
              type="button"
              onClick={() => update('products.collections', collections.filter((_, idx) => idx !== i))}
              className="text-xs font-mono uppercase text-red-700 hover:underline mt-6"
              data-testid={`remove-collection-${i}`}
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addCollection}
          className="px-4 py-2 border border-yunti-ink text-xs font-mono uppercase tracking-widest hover:bg-yunti-ink hover:text-white transition-colors"
          data-testid="add-collection"
        >
          + Add collection
        </button>
      </Card>

      {items.map((product, i) => (
        <ProductItem
          key={product.id}
          product={product}
          index={i}
          collections={collections}
          onChange={(key, v) => setItem(i, key, v)}
          onRemove={() => update('products.items', items.filter((_, idx) => idx !== i))}
        />
      ))}

      <button
        type="button"
        onClick={addItem}
        className="px-4 py-2 border border-yunti-ink text-xs font-mono uppercase tracking-widest hover:bg-yunti-ink hover:text-white transition-colors"
        data-testid="add-product"
      >
        + Add product
      </button>
    </div>
  );
}
